import { useState, useEffect } from "react";
import { KeyValueObj } from "../common/types";
import { useAxiosAuth } from "./useAxiosAuth";
import useLogout from "./useLogout";
import { isCurrentToken } from "../common/utils/tokenStorage";

interface UseQueryReturn<T> {
  data: T;
  error: string;
  loading: boolean;
  meta: KeyValueObj;
  refetch: (url?: string) => void;
}

const useQuery = <T = any>(
  url: string,
  enabled: boolean = true
): UseQueryReturn<T> => {
  const [response, setResponse] = useState<T>([] as T);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<KeyValueObj>({});
  const [requestUrl, setrequestUrl] = useState("");
  const logout = useLogout();

  const axiosAuth = useAxiosAuth();
  useEffect(() => {
    if (enabled) {
      axiosCall(url);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);

  async function axiosCall(url: string) {
    setrequestUrl(url);
    setLoading(true);
    try {
      const res = await axiosAuth.get(url);
      if (res.data.success) {
        setMeta(res.data.meta);
        setResponse(res.data.data);
        setError("");
      } else {
        setError("Something went wrong");
      }
    } catch (err: any) {
      if (
        err?.response?.status === 401 &&
        isCurrentToken(err?.config?.headers?.authorization)
      ) {
        return logout();
      }
      // MED-675: was `setError(err)` — the raw error/exception object into a
      // string-typed field. Harmless for DataWrapper (it only checks
      // truthiness), but it discarded the actual failure reason, making a
      // real fetch failure indistinguishable from any other in the console.
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  function refetch(url?: string) {
    axiosCall(url || requestUrl);
  }
  return { data: response, error, loading, meta, refetch };
};

export default useQuery;
